import { useEffect, useMemo, useRef, useState } from 'react';
import { useIdentity } from '../features/identity/state/useIdentity';
import './BusinessViewSwitcher.css';

/**
 * The global "Business View" trigger + in-shell company switcher — see docs/business-view-switcher.md
 * and the supplied Business Profile Selection design package (EPN-47) for the approved visual source of
 * truth. Shared between `app/AppShell.tsx`'s topbar and the standalone Dashboard's own header
 * (`features/dashboard-v2/components/TopHeader.tsx`), the same pattern `shared/ProfileMenu.tsx` already
 * uses for those same two hosts — one implementation, not a per-host reimplementation.
 *
 * Owns ONLY company selection/switching. Account/profile actions (My Profile, Change Password, Sign out,
 * etc.) remain `ProfileMenu`'s responsibility — see docs/profile-menu-architecture.md.
 *
 * Reads `me`/`switchCompany` from `useIdentity()` — no second company list is fetched here;
 * `me.memberships` (from `GET /api/me`) already carries everything this component needs.
 */
export function BusinessViewSwitcher() {
  const { me, switchCompany, error } = useIdentity();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [switchingMembershipId, setSwitchingMembershipId] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const activeMembership = useMemo(
    () => me?.memberships.find((m) => m.companyId === me.activeCompanyId) ?? null,
    [me],
  );

  // Pinned current-company row is never part of the searchable/switchable set — see BUSINESS_LIST_RULES
  // in the design reconciliation: "current" and "Switch to:" are two structurally distinct groups.
  const otherMemberships = useMemo(
    () => me?.memberships.filter((m) => m.companyId !== me.activeCompanyId) ?? [],
    [me],
  );

  const filteredOtherMemberships = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return otherMemberships;
    return otherMemberships.filter((m) => m.companyName.toLowerCase().includes(query));
  }, [otherMemberships, search]);

  // Route/company changes close the popover — a stale open dropdown pointing at the previous company's
  // context would be confusing, the same reasoning ProfileMenu's own effect documents.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setOpen(false);
  }, [me?.activeCompanyId]);

  useEffect(() => {
    if (!open) return;

    function handlePointerDown(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
        return;
      }
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      const items = popoverRef.current?.querySelectorAll<HTMLElement>('[data-business-view-item]');
      if (!items || items.length === 0) return;
      event.preventDefault();
      const currentIndex = Array.prototype.indexOf.call(items, document.activeElement);
      const nextIndex = event.key === 'ArrowDown'
        ? (currentIndex + 1) % items.length
        : (currentIndex - 1 + items.length) % items.length;
      items[nextIndex]?.focus();
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      searchRef.current?.focus();
    } else {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSearch('');
    }
  }, [open]);

  if (!me || !activeMembership) {
    return null;
  }

  async function handleSwitch(membershipId: string) {
    if (switchingMembershipId) return; // Prevent a second switch while one is already in flight.
    setSwitchingMembershipId(membershipId);
    try {
      await switchCompany(membershipId);
      // Only reached once switchCompany()'s own identity refresh has actually succeeded — never
      // announced/closed on an optimistic assumption. A rejected switch falls through to the catch below,
      // leaving the popover open with the identity context's `error` rendered.
      setOpen(false);
      setSearch('');
    } catch {
      // Error is already surfaced via `error` from useIdentity() — nothing else to do here. The active
      // company/trigger presentation is untouched, since switchCompany() only ever changes `me` on
      // success.
    } finally {
      setSwitchingMembershipId(null);
    }
  }

  function handleSearchXClick() {
    if (search) {
      setSearch('');
      searchRef.current?.focus();
    } else {
      setOpen(false);
      triggerRef.current?.focus();
    }
  }

  const hasOtherMemberships = otherMemberships.length > 0;
  const hasResults = filteredOtherMemberships.length > 0;

  return (
    <div className="business-view" ref={containerRef}>
      <button
        type="button"
        ref={triggerRef}
        className="business-view-trigger"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={`Business View — active company ${activeMembership.companyName}`}
      >
        <i className="fa fa-building-o business-view-trigger-icon" aria-hidden="true" />
        <span className="business-view-trigger-text">
          <span className="business-view-trigger-label">Business View</span>
          <span className="business-view-trigger-name">{activeMembership.companyName}</span>
        </span>
        <i className={`fa fa-chevron-down business-view-chevron${open ? ' business-view-chevron--open' : ''}`} aria-hidden="true" />
      </button>

      {open && (
        <div className="business-view-popover" ref={popoverRef} data-testid="business-view-popover">
          <div className="business-view-search">
            <i className="fa fa-search business-view-search-icon" aria-hidden="true" />
            <input
              ref={searchRef}
              type="text"
              className="business-view-search-input"
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search companies"
              data-business-view-item
            />
            <button
              type="button"
              className="business-view-search-clear"
              onClick={handleSearchXClick}
              aria-label={search ? 'Clear search' : 'Close Business View'}
            >
              <i className="fa fa-times" aria-hidden="true" />
            </button>
          </div>

          {error && (
            <p className="business-view-error" role="alert">
              {error}
            </p>
          )}

          <div className="business-view-current" aria-current="true">
            <span className="business-view-current-name">{activeMembership.companyName}</span>
            <i className="fa fa-check business-view-current-check" aria-hidden="true" />
          </div>

          {hasOtherMemberships && (
            <>
              <p className="business-view-section-label">Switch to:</p>
              {hasResults ? (
                <ul className="business-view-list">
                  {filteredOtherMemberships.map((m) => (
                    <li key={m.membershipId}>
                      <button
                        type="button"
                        className="business-view-item"
                        disabled={switchingMembershipId !== null}
                        onClick={() => void handleSwitch(m.membershipId)}
                        data-business-view-item
                      >
                        {m.companyName}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="business-view-no-results">No Results Found</p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
